import { useRef, useState } from "react";
import { uploadAdminListingImage } from "@/services/adminApi";
import { uploadUrl } from "@/services/config";
import type { BusinessPhoto } from "@/types";

interface PhotoManagerProps {
  photos: BusinessPhoto[];
  onChange: (photos: BusinessPhoto[]) => void;
  token: string | null;
  disabled?: boolean;
}

/**
 * Photo manager for the admin business editor.
 *
 * Uploads real files through POST /api/admin/listing-upload (nothing is stored
 * in a browser temp/object URL), and lets the admin drag-to-reorder, set the
 * cover (first photo), reorder with the arrow buttons, or remove a photo.
 */
export default function PhotoManager({ photos, onChange, token, disabled = false }: PhotoManagerProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const dragIndex = useRef<number | null>(null);
  const [urlInput, setUrlInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2200);
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError("");
    setBusy(true);
    try {
      const url = await uploadAdminListingImage(file, token);
      onChange([...photos, { id: `ph-${Date.now()}`, url, caption: file.name }]);
      flash("Photo uploaded.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed. Please try another image.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addByUrl = () => {
    const value = urlInput.trim();
    if (!value) return;
    onChange([...photos, { id: `ph-${Date.now()}`, url: value }]);
    setUrlInput("");
    flash("Photo added.");
  };

  const remove = (id: string) => {
    onChange(photos.filter((p) => p.id !== id));
    flash("Photo removed.");
  };

  const move = (from: number, to: number) => {
    if (from === to || to < 0 || to >= photos.length) return;
    const next = [...photos];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const makeCover = (index: number) => {
    if (index === 0) return;
    const next = [...photos];
    const [moved] = next.splice(index, 1);
    next.unshift(moved);
    onChange(next);
    flash("Cover photo updated.");
  };

  return (
    <section className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-heading text-base font-bold text-foreground-950">Photos</h2>
          <p className="mt-1 text-xs text-foreground-500">
            The first photo is the cover. Drag to reorder, or upload new images from your device.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-foreground-600">
            <span className="font-semibold text-foreground-950">{photos.length}</span> photo{photos.length === 1 ? "" : "s"}
          </span>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={disabled || busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-3.5 py-2 text-sm font-semibold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer"
          >
            <i className={busy ? "ri-loader-4-line animate-spin" : "ri-upload-2-line"}></i>
            {busy ? "Uploading…" : "Upload photo"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 p-3 text-xs text-secondary-900">
          <i className="ri-error-warning-line text-secondary-600"></i>
          <span>{error}</span>
        </div>
      )}
      {notice && !error && (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 p-3 text-xs text-accent-900">
          <i className="ri-checkbox-circle-line text-accent-600"></i>
          <span>{notice}</span>
        </div>
      )}

      {photos.length === 0 ? (
        <div className="mt-4 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-12 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-background-200 text-foreground-500">
            <i className="ri-image-add-line text-2xl"></i>
          </span>
          <p className="mt-3 font-heading text-sm font-bold text-foreground-950">No photos yet</p>
          <p className="mt-1 text-xs text-foreground-500">Upload a photo or paste an image URL below.</p>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((photo, index) => (
            <figure
              key={photo.id}
              draggable={!disabled}
              onDragStart={() => {
                dragIndex.current = index;
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragIndex.current !== null) move(dragIndex.current, index);
                dragIndex.current = null;
              }}
              onDragEnd={() => {
                dragIndex.current = null;
              }}
              className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 cursor-grab active:cursor-grabbing"
            >
              <div className="relative aspect-[4/3] w-full overflow-hidden">
                <img
                  src={uploadUrl(photo.url)}
                  alt={photo.caption || `Photo ${index + 1}`}
                  className="h-full w-full object-cover"
                />
                {index === 0 && (
                  <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-primary-500 px-2 py-0.5 text-[10px] font-bold text-foreground-950">
                    <i className="ri-image-2-line"></i> Cover
                  </span>
                )}
                <span className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md bg-background-50/90 text-foreground-500">
                  <i className="ri-drag-move-2-line"></i>
                </span>
              </div>
              <figcaption className="p-2.5">
                <p className="truncate text-[11px] font-medium text-foreground-600">{photo.caption || photo.url}</p>
                <div className="mt-2 flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => makeCover(index)}
                    disabled={index === 0 || disabled}
                    className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-background-300 px-2 py-1.5 text-[11px] font-semibold text-foreground-700 hover:border-primary-400 disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-star-line"></i> Cover
                  </button>
                  <button
                    type="button"
                    onClick={() => move(index, index - 1)}
                    disabled={index === 0 || disabled}
                    aria-label="Move left"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => move(index, index + 1)}
                    disabled={index === photos.length - 1 || disabled}
                    aria-label="Move right"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                  >
                    <i className="ri-arrow-right-line"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(photo.id)}
                    disabled={disabled}
                    aria-label="Remove photo"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-secondary-200 text-secondary-700 hover:bg-secondary-50 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <input
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          placeholder="…or paste an image URL"
          className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
        />
        <button
          type="button"
          onClick={addByUrl}
          disabled={!urlInput.trim() || disabled}
          className="inline-flex items-center justify-center gap-1.5 rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-800 hover:border-primary-400 disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line"></i> Add
        </button>
      </div>
    </section>
  );
}